// Instelbare breedtes van de desktop (ticket 28376, wens van 30 oktober 2026): de gebruiker sleept de rand tussen twee
// onderdelen en de breedte blijft bewaard, ook na herladen en op een ander apparaat (de instelling `layout` in Settings, api.ts).
// De gesprekkenkolom, de werkerskolom en het mailpaneel naast het gesprek in px; de verdeling tussen de twee panelen als deel van
// de breedte van beide panelen dat het linkerpaneel krijgt. Op de telefoon (tot 900 px) is er niets in te stellen. De client
// houdt daarnaast het paneel naast een onderdeel minstens 480 px breed (client/src/state/layout.ts).
// Sinds ticket 28625 (het leesvenster van de mail groter maken) ook de maillijst op het tabblad Mail (de lezer ernaast houdt
// 480 px), en mag het mailpaneel naast een gesprek tot 1400 px (het gesprek houdt 480 px). Sinds deel 2 van dat ticket (het
// leesvenster onder de lijst, zoals in Outlook) ook de hoogte van de lijst boven de lezer, als deel van de hoogte van lijst en
// lezer samen (de client houdt de lezer minstens 240 px en de lijst 120 px hoog), en in het mailpaneel van de chat de hoogte
// van de gevonden mails boven de mail (de mail minstens 240 px, de lijst 96 px). Sinds 2.2.50 (ticket 30961) ook de kolom
// Gevlagd helemaal links, links van de gesprekkenkolom (dezelfde grenzen; de rest houdt 480 px).

/** Per onderdeel de grenzen en de standaard. De standaard is de breedte van vóór ticket 28376: de kolommen 280 px, de panelen
 * half om half, en het mailpaneel 420 px (het was `clamp(340px, 38%, 520px)` van het chatscherm, en dat is 424 px op 1440 px met
 * de werkerskolom open). De maillijst (ticket 28625) was `clamp(300px, 32vw, 420px)`, dus 420 px op 1440 px; de standaard is
 * 380 px, zodat de lezer op 1440 px met de mappen, de werkerskolom en de strook van de gesprekken 480 px houdt. */
export const LAYOUT_LIMITS = {
  chatColumn: { min: 220, max: 480, default: 280 },
  /** De kolom Gevlagd helemaal links (ticket 30961): even breed als de gesprekkenkolom. */
  flaggedColumn: { min: 220, max: 480, default: 280 },
  workersRail: { min: 220, max: 480, default: 280 },
  mailPane: { min: 300, max: 1400, default: 420 },
  mailList: { min: 300, max: 640, default: 380 },
  paneSplit: { min: 0.25, max: 0.75, default: 0.5 },
  /** Het leesvenster onder de lijst (ticket 28625, deel 2): het deel van de hoogte voor de lijst; standaard 40 procent. */
  mailSplit: { min: 0.2, max: 0.8, default: 0.4 },
  /** Het mailpaneel in de chat (ticket 28625, aanvulling): het deel van zijn hoogte voor de gevonden mails boven de mail; standaard
   * 45 procent, zoals de vaste `max-h-[45%]` van daarvoor. */
  mailPaneSplit: { min: 0.2, max: 0.8, default: 0.45 },
} as const;

export type LayoutKey = keyof typeof LAYOUT_LIMITS;
/** De verdelingen van een hoogte (ticket 28625): het deel voor de lijst boven de lezer, en voor de gevonden mails boven de mail. */
export type LayoutHeightKey = 'mailSplit' | 'mailPaneSplit';
/** De onderdelen met een breedte in px; de verdeling tussen de panelen (0,25 tot 0,75) en de verdelingen van een hoogte (0,2 tot
 * 0,8) zijn een deel. */
export type LayoutWidthKey = Exclude<LayoutKey, 'paneSplit' | LayoutHeightKey>;

/** De verdelingen: een deel van de ruimte, op drie decimalen; de rest is een breedte in hele px. */
const SPLITS: ReadonlySet<LayoutKey> = new Set<LayoutKey>(['paneSplit', 'mailSplit', 'mailPaneSplit']);

/** De standaardindeling (een nieuw object per aanroep). */
export function defaultLayout(): Record<LayoutKey, number> {
  return { chatColumn: LAYOUT_LIMITS.chatColumn.default, flaggedColumn: LAYOUT_LIMITS.flaggedColumn.default, workersRail: LAYOUT_LIMITS.workersRail.default, mailPane: LAYOUT_LIMITS.mailPane.default, mailList: LAYOUT_LIMITS.mailList.default, paneSplit: LAYOUT_LIMITS.paneSplit.default, mailSplit: LAYOUT_LIMITS.mailSplit.default, mailPaneSplit: LAYOUT_LIMITS.mailPaneSplit.default };
}

/** Een waarde binnen de grenzen van dat onderdeel: een breedte als heel getal px, een verdeling op drie decimalen. */
export function clampLayout(key: LayoutKey, value: number): number {
  const { min, max } = LAYOUT_LIMITS[key];
  const v = Math.min(max, Math.max(min, value));
  return SPLITS.has(key) ? Math.round(v * 1000) / 1000 : Math.round(v);
}
